/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/auth/RequireVisitor.tsx
 *
 * Rôle :
 *   Garde de route pour les pages PERSONNELLES visiteur (Mon espace,
 *   Mon badge, Mon programme, Mon parcours, Mes favoris, Mon groupe,
 *   Ma carte). Sans session TNEVSID ces pages n'ont aucun sens et
 *   affichaient des écrans vides/cassés — ce qui faisait croire à Mika
 *   que « rien ne marche ». Désormais : non connecté → /connexion
 *   (avec `?from=` pour revenir après login), connecté → la page.
 *
 *   Pattern calqué sur `components/admin/RoleGuard` (cohérence §17),
 *   mais en garde de LAYOUT (`<Outlet/>`) pour grouper N routes en un
 *   seul point dans App.tsx (faible churn, faible risque).
 *
 *   §8.6 : aucune UI inventée — on ne fait que rediriger.
 *
 * Réutilisable : 100 % (toute app à session « visiteur »).
 *
 * Voir : hooks/useVisitorSession.ts, api/me/badge.php,
 *        CLAUDE.md §24 entrée 2026-05-17.
 *
 * @author Mika
 * @since  2026-05-17
 */

import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useVisitorSession } from '@/hooks/useVisitorSession';

const centerStyle = {
  minHeight: '60vh',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1rem',
  padding: '2rem 1.25rem',
  textAlign: 'center' as const,
  color: 'var(--tne-color-text-strong, #2A1212)',
  fontFamily: 'Roboto, system-ui, sans-serif',
};

export default function RequireVisitor() {
  // FIX 2026-05-18 (bug Mika « /connexion tourne en rond ») — on
  // distingue désormais `error` (serveur HS) de `anonymous`. Sur
  // erreur serveur on N'ENVOIE PLUS vers /connexion (sinon : login →
  // code OK → navigate /mon-espace → me/badge.php 500 → /connexion →
  // BOUCLE). Cf. CLAUDE.md §24 2026-05-18 + RoleGuard (même principe).
  const { status, refetch } = useVisitorSession();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div role="status" aria-live="polite" style={centerStyle}>
        <FormattedMessage id="common.loading" defaultMessage="Chargement…" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <main role="alert" style={centerStyle}>
        <h1
          style={{
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            fontWeight: 800,
            fontSize: '1.4rem',
            color: 'var(--tne-color-primary, #8e1b1b)',
            margin: 0,
          }}
        >
          <FormattedMessage id="requireVisitor.error.title" />
        </h1>
        <p style={{ maxWidth: '30rem', lineHeight: 1.55, margin: 0 }}>
          <FormattedMessage id="requireVisitor.error.body" />
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={refetch}
            style={{
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              fontWeight: 600,
              color: '#fff',
              background: 'var(--tne-color-primary, #8e1b1b)',
              border: 'none',
              borderRadius: '0.75rem',
              padding: '0.65rem 1.4rem',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage id="requireVisitor.error.retry" />
          </button>
          <Link
            to="/connexion"
            style={{
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              fontWeight: 600,
              color: 'var(--tne-color-primary, #8e1b1b)',
              textDecoration: 'underline',
              padding: '0.65rem 0.6rem',
            }}
          >
            <FormattedMessage id="requireVisitor.error.toLogin" />
          </Link>
        </div>
      </main>
    );
  }

  if (status !== 'authenticated') {
    // FIX 2026-05-18 — Filet anti-boucle Safari privé : si un verify
    // serveur a réussi il y a < 2 min (marqueur posé UNIQUEMENT par une
    // réponse `phase:'verified'`), on ne rebondit PAS vers /connexion sur
    // une 401 transitoire le temps que Safari (mode privé / ITP) commite
    // le cookie TNEVSID. Sinon : code OK → navigate → me/badge 401 (cookie
    // pas encore écrit) → /connexion → re-saisie mail → BOUCLE. Le cookie
    // se pose dans la foulée ; les appels API suivants le porteront.
    let recentVerify = false;
    try {
      const raw = sessionStorage.getItem('tne_visitor_session');
      if (raw) {
        const m = JSON.parse(raw) as { loggedInAt?: number };
        if (
          typeof m.loggedInAt === 'number' &&
          Date.now() - m.loggedInAt < 120_000
        ) {
          recentVerify = true;
        }
      }
    } catch {
      /* marqueur illisible → comportement normal */
    }

    if (recentVerify) {
      return <Outlet />;
    }

    // status === 'anonymous' : redirection LÉGITIME (pas de session).
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/connexion?from=${from}`} replace />;
  }

  return <Outlet />;
}
